/**
 * Report brief. The first sentences stay visible. The rest of the essay
 * opens in place. Owners can still generate or rewrite it.
 */

import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { apiCall } from "../../services/api";
import { briefFromSummary } from "../../utils/reportPresentation";

const markdownComponents = {
  h1: ({ children }) => (
    <h2 className="mb-3 mt-6 text-xl font-semibold text-foreground first:mt-0">
      {children}
    </h2>
  ),
  h2: ({ children }) => (
    <h3 className="mb-2 mt-5 text-lg font-semibold text-foreground">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="mb-2 mt-4 text-base font-medium text-foreground">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-2 pl-5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-2 pl-5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-sm text-muted-foreground">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline"
    >
      {children}
    </a>
  ),
};

export function ExecutiveSummary({
  summary,
  domain,
  jobId,
  onSummaryGenerated,
  readOnly = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const canGenerate = Boolean(jobId) && !readOnly;
  const { brief, hasMore } = briefFromSummary(summary);

  const handleGenerateSummary = async () => {
    if (!jobId || readOnly) return;

    setIsGenerating(true);
    setError(null);

    try {
      const result = await apiCall(`/jobs/${jobId}/generate-executive-summary`, {
        method: "POST",
        body: { force: true, domain },
      });

      if (result.executive_summary && onSummaryGenerated) {
        onSummaryGenerated(result.executive_summary);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="mb-8">
      {brief && (
        <p className="text-base leading-relaxed text-foreground">{brief}</p>
      )}
      {!brief && !summary && (
        <p className="text-sm text-muted-foreground">No brief yet.</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-4">
        {hasMore && (
          <button
            type="button"
            onClick={() => setIsExpanded((open) => !open)}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Hide the rest" : "Read the rest"}
          </button>
        )}
        {canGenerate && (
          <button
            type="button"
            onClick={handleGenerateSummary}
            disabled={isGenerating}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {isGenerating
              ? "Writing..."
              : summary
                ? "Regenerate"
                : "Generate Executive Summary"}
          </button>
        )}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      {isExpanded && summary && (
        <div className="mt-5 border-t border-border pt-5">
          <ReactMarkdown components={markdownComponents}>{summary}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}
